import { z } from "zod";

/**
 * Recommendation — a capability suggestion surfaced to the user (rendered as a chip on the frontend).
 *
 * Cross-boundary contract: the backend ranks the recommendations registry
 * (langgraph_app/app/recommendations), the frontend renders every entry it
 * receives as a chip, and a click becomes a plain user message carrying the
 * structure back on message_metadata. Resolution (skill / tool) never leaves
 * the backend — the click carries only the recommendation id.
 *
 * The render mode falls out of the fields (mirroring the registry contract in
 * plans/recommendations.md): no `collects` and no `component` → prompt chip (the
 * text IS the message); `collects` alone → simple inputs; a `component` →
 * custom render (a registered recommendation renderer, e.g. DomainPicker).
 */

/**
 * The bespoke panel components a recommendation may declare — the single
 * source of truth both sides are tested against, so a registry change can
 * never ship a recommendation the UI can't render:
 *
 *   - backend (registries.test): every available recommendation's
 *     `component` is null or in this list
 *   - frontend (recommendationPanels.test): every name here has a Panel in
 *     the injected RECOMMENDATION_PANELS mapping
 */
export const RECOMMENDATION_COMPONENTS = ["DomainPicker", "ColorPicker", "Testimonials", "LeadMagnetForm"] as const;
export type RecommendationComponent = (typeof RECOMMENDATION_COMPONENTS)[number];

/**
 * What a collects field accepts. `text` (default) renders a text input;
 * `image` renders a drop square wired to the shared upload pipeline;
 * `choice` renders a segmented control over `options`. All yield string
 * values into `collected` — a text field its text, an image field the
 * uploaded URL, a choice field the picked option — so the click contract
 * stays string-shaped (`multiple` makes it a string[]).
 */
export const COLLECTS_ACCEPTS = ["text", "image", "choice"] as const;
export type CollectsAccepts = (typeof COLLECTS_ACCEPTS)[number];

/**
 * An input a clicked recommendation gathers before sending. The skill owns
 * its conversational copy: `prompt` is the question the input asks and
 * `placeholder` shows the usual scenarios. A bare string is shorthand for
 * { field } with a "?" suffix marking optional.
 *
 * `accepts` picks the input kind (text | image | choice); `multiple`
 * collects a list (e.g. a gallery's images); `fields` makes it a group
 * (repeatable records). See plans/recommendations.md ("Collected input").
 */
/**
 * A subfield of a `group` field — one column of a repeatable record
 * (an FAQ's question, a pricing tier's price). Text-only: load-bearing
 * media inside records stays a top-level image field (or a bespoke
 * component, like Testimonials' headshots).
 */
export const collectsSubfieldSchema = z.object({
  field: z.string(),
  prompt: z.string(),
  placeholder: z.string().optional(),
  optional: z.boolean().optional(),
});
export type CollectsSubfield = z.infer<typeof collectsSubfieldSchema>;

export const collectsFieldSchema = z.object({
  field: z.string(),
  prompt: z.string(),
  placeholder: z.string().optional(),
  optional: z.boolean().optional(),
  accepts: z.enum(COLLECTS_ACCEPTS).optional(), // default "text"
  multiple: z.boolean().optional(),
  /** `choice` fields: the segmented options and the one preselected on open. */
  options: z.array(z.string()).optional(),
  default: z.string().optional(),
  /**
   * `group` kind: declaring `fields` makes this a repeatable record — the
   * generic panel renders + add / − remove cards (an FAQ's {question,
   * answer}, a pricing tier's {title, price, description}). Projects into
   * `collected` as an array of records.
   */
  fields: z.array(collectsSubfieldSchema).optional(),
  /** What one record is called — drives "+ Add <item_label>". */
  item_label: z.string().optional(),
});
export type CollectsField = z.infer<typeof collectsFieldSchema>;

/**
 * A collected value: a scalar field's string, a `multiple` field's list,
 * or a group field's records.
 */
export type CollectedValue = string | string[] | Array<Record<string, string>>;

export const recommendationSchema = z.object({
  /** Registry id — the recommendation entry ("website/lead-form"). */
  recommendationId: z.string(),
  /** First-person label; the chip text and panel title. */
  text: z.string(),
  /** The engineered prompt a prompt chip seeds on prefill; falls back to text. */
  prompt: z.string().optional(),
  /** Panel-header subhead under the title. */
  description: z.string().optional(),
  /** Registered renderer name for custom render, or null. Not an artifact. */
  component: z.string().nullable(),
  /** Registry-declared icon name (lucide kebab-case); frontend maps it. */
  icon: z.string().nullable().optional(),
  /** Pinned ordering: lower shows earlier; unpinned chips follow, short-first. */
  priority: z.number().optional(),
  /** Inputs to collect before sending — names or conversational field specs. */
  collects: z.array(z.union([z.string(), collectsFieldSchema])).optional(),
});
export type Recommendation = z.infer<typeof recommendationSchema>;
